import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/cn";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  fetchCreditBalance,
  fetchCreditPacks,
  fetchCreditHistory,
  setPurchasingPackId,
} from "@/store/slices/aiCreditsSlice";
import stripeService from "@/services/stripeService";
import { captureException } from "@/config/sentry";

// Affichage utilisateur : tout est en euros (« crédits »), même si l'unité
// interne du budget IA est le dollar (facturation Anthropic) — parité
// d'affichage 1 crédit-€ = 1 $ de budget interne, invisible pour l'utilisateur.
function formatEur(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// Section « Crédits IA » de l'onglet Abonnement : solde, packs achetables
// (checkout Stripe one-shot) et historique d'achats. Les crédits sont
// consommés automatiquement une fois le quota mensuel inclus épuisé.
export const AiCreditsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { balance, packs, history, loading, purchasingPackId, error } =
    useAppSelector((s) => s.aiCredits);

  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";

  useEffect(() => {
    dispatch(fetchCreditBalance());
    dispatch(fetchCreditPacks());
    dispatch(fetchCreditHistory());
  }, [dispatch]);

  // Retour d'un checkout annulé (cancel_url Stripe)
  useEffect(() => {
    if (searchParams.get("credits_cancelled") === "true") {
      toast.info(t("aiCredits.purchaseCancelled"));
      searchParams.delete("credits_cancelled");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuy = async (packId: string) => {
    if (purchasingPackId) return; // anti double-clic
    dispatch(setPurchasingPackId(packId));
    try {
      const response = await stripeService.createAiCreditCheckout({ packId });
      const url = (response as any)?.data?.url;
      if (url) {
        window.location.href = url;
        return; // on quitte la page — laisser le bouton en loading
      }
      throw new Error("Missing checkout URL in response");
    } catch (err) {
      captureException(err as Error, {
        operation: "ai_credit_checkout_creation",
        packId,
        financial_impact: true,
      });
      toast.error(t("aiCredits.checkoutError"));
      dispatch(setPurchasingPackId(null));
    }
  };

  return (
    <Card className="border-border/60 overflow-hidden">
      <CardContent className="p-6 md:p-8 space-y-6">
        {/* Header : titre + solde */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {t("aiCredits.sectionLabel")}
              </p>
            </div>
            <p className="text-sm text-muted-foreground max-w-xl">
              {t("aiCredits.sectionDescription")}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {t("aiCredits.balanceLabel")}
            </p>
            <p className="text-3xl font-bold tabular-nums leading-tight">
              {formatEur(balance?.balanceUsd ?? 0, locale)}
            </p>
          </div>
        </div>

        {error && (
          <div
            className="flex items-start gap-3 p-3 rounded-lg border border-muted bg-muted/30"
            role="alert"
          >
            <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              {t("aiCredits.loadError")}
            </p>
          </div>
        )}

        {/* Packs */}
        {loading && packs.length === 0 ? (
          <div className="grid gap-4 sm:grid-cols-3" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-36 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          packs.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-3">
              {packs.map((pack) => {
                const isBuying = purchasingPackId === pack.id;
                return (
                  <div
                    key={pack.id}
                    className="flex flex-col rounded-lg border border-border/60 p-4 gap-3"
                  >
                    <p className="text-sm font-semibold truncate">{pack.name}</p>
                    <p className="text-2xl font-bold tabular-nums">
                      {formatEur(pack.priceEur, locale)}
                    </p>
                    {pack.description && (
                      <p className="text-xs text-muted-foreground">
                        {pack.description}
                      </p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      className="mt-auto cursor-pointer transition-colors duration-150"
                      disabled={Boolean(purchasingPackId)}
                      onClick={() => handleBuy(pack.id)}
                    >
                      {isBuying ? t("aiCredits.redirecting") : t("aiCredits.buy")}
                    </Button>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Historique : achats (vert) ET consommations (débits, rouge) —
            l'utilisateur voit où passent ses crédits, avec le solde après
            chaque mouvement. */}
        {history.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {t("aiCredits.historyLabel")}
              </p>
            </div>
            <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
              {history.map((tx) => (
                <li
                  key={tx.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                >
                  <span className="truncate font-medium">
                    {tx.type === "purchase"
                      ? (tx.packName ?? t("aiCredits.sectionLabel"))
                      : t("aiCredits.usageDebit")}
                  </span>
                  <span className="text-muted-foreground whitespace-nowrap">
                    {formatDate(tx.createdAt, locale)}
                  </span>
                  <span className="text-right whitespace-nowrap">
                    <span
                      className={cn(
                        "tabular-nums font-medium",
                        tx.type === "purchase"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-red-600 dark:text-red-400",
                      )}
                    >
                      {tx.type === "purchase" ? "+" : ""}
                      {formatEur(tx.amountUsd, locale)}
                    </span>
                    {tx.balanceAfterUsd != null && (
                      <span className="block text-[11px] text-muted-foreground tabular-nums">
                        {t("aiCredits.balanceAfter", {
                          amount: formatEur(tx.balanceAfterUsd, locale),
                        })}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
